import dataIntegrations from 'routes/Integrations/mockObject'

import { TAG_MAPPINGS_VALUES, DEFAULT_LIMIT, API_TIMEOUT_IN_SECONDS } from 'Constants'
import basicAuth, { cleanupTokens } from './basicAuth'
import { GetLedgerResponse } from './Api.types'

// TODO: replace mockobject with real api calls

type UNPAID = '12016'

type PAID = '5941'

type PARTIAL = '12010'

export type PaymentStatus = UNPAID | PAID | PARTIAL

type INVOICE = '3'

export type TODO_DOCUMENT_TYPES = INVOICE

type APPROVED = '12149'

export type TODO_WORKFLOW_STATUSES = APPROVED

export type DOCUMENT_FILTERS = {
  limit: number
  offset: number
  paymentStatuses?: PaymentStatus[]
  documentTypes?: TODO_DOCUMENT_TYPES[]
  workflowStatuses?: TODO_WORKFLOW_STATUSES[]
}

export const BASE_URL = process.env.REACT_APP_API_BASE_URL

const { GL_CODE_TAG, MY_VENDOR_TAG, EXPENSE_CATEGORY_TAG } = TAG_MAPPINGS_VALUES

const authorizedFetch = basicAuth({
  fetch,
  storage: localStorage,
})

const call = async <B = any, H = any>(
  uri: string,
  methodOrOptions,
  body?: Record<string, any>,
  authorized = true,
  redirectOnUnauthorized = true,
) => {
  const fetchMethod = authorized ? authorizedFetch : fetch

  const controller = new AbortController()
  setTimeout(() => controller.abort(), API_TIMEOUT_IN_SECONDS * 1000)

  const response = await fetchMethod(uri, { ...methodOrOptions, signal: controller.signal }, body)
  if (redirectOnUnauthorized && response.status === 401 && methodOrOptions.login !== true) {
    await cleanupTokens(localStorage)
    global.location = '/'
  }

  if (response.ok) {
    return response.json()
  }

  const text = await response.text()
  const error = new Error(text)
  error.status = response.status
  error.statusText = response.statusText
  throw error
}

export const login = async (email, password) => {
  const response = await call(
    `${BASE_URL}/sessions`,
    {
      login: true,
      method: 'post',
    },
    {
      email,
      password,
    },
  )
  return response
}

export const registerUser = (data) =>
  call(
    `${BASE_URL}/users`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    undefined,
    false,
  )

export const resendAccountVerificationEmail = (data) =>
  call(`${BASE_URL}/users/validate/email`, { method: 'post' }, data)

export const getDocumentStats = (data) =>
  call(`${BASE_URL}/stats/documents/search`, { method: 'post' }, data)

export const verifyAccountEmail = (data) =>
  call(
    `${BASE_URL}/users/email/validated`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    null,
    false,
  )

export const updateUser = (data) => call(`${BASE_URL}/users`, { method: 'put' }, data)

export const getS3SignedUrl = (data) =>
  call(`${BASE_URL}/documents/images/presignedurls`, { method: 'post' }, data)

export const createFileMetadata = (data) => call(`${BASE_URL}/documents`, { method: 'post' }, data)

export const updateReceipt = (guid, data) =>
  call(`${BASE_URL}/documents/${guid}`, { method: 'put' }, data)

export const getDropboxOauthUrl = () =>
  call(`${BASE_URL}/dropbox/url`, { method: 'get' }, '', false)

export const getOffice365AccessToken = (data) =>
  call(
    `${BASE_URL}/office365/tokenexchange`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    '',
    false,
  )

export const getLedgerFilters = () => call(`${BASE_URL}/filters/search`, { method: 'post' })

export const getLedgerRecords = ({
  limit,
  offset,
  ...filters
}: DOCUMENT_FILTERS): Promise<GetLedgerResponse> => {
  return call(
    `${BASE_URL}/documents/search?limit=${limit || DEFAULT_LIMIT}&offset=${offset || 0}`,
    { method: 'post' },
    { filters },
  )
}

/**
 * @param {Array<string>} guids
 */
export function exportInvoicePayments(guids) {
  return call(`${BASE_URL}/exports/payments`, { method: 'POST', body: JSON.stringify({ guids }) })
}

export const getUserProfile = () => call(`${BASE_URL}/users`, { method: 'get' })

export const getLedgerRecordByGuid = (guid) =>
  call(`${BASE_URL}/documents/${guid}`, { method: 'get' })

export const getExpenseCategories = () =>
  call(`${BASE_URL}/expensecategories/search`, { method: 'post' })

export const getCurrencies = () => call(`${BASE_URL}/currencies`, { method: 'get' })

export const updateSendForHumanReviewSettingApi = ({ ...params }) =>
  call(`${BASE_URL}/settings/send_for_human_review`, { method: 'put' }, params)

export const getJobCodes = () => call(`${BASE_URL}/jobcodes/search`, { method: 'post' })

export const getGLCodes = () => call(`${BASE_URL}/glcodes/search`, { method: 'post' })

export const createJobCode = (params) =>
  call(
    `${BASE_URL}/jobcodes`,
    {
      method: 'post',
    },
    params,
  )

export const getPaymentMethods = () => call(`${BASE_URL}/paymenttypes/search`, { method: 'post' })

export const createTag = (params) =>
  call(
    `${BASE_URL}/tags`,
    {
      method: 'post',
    },
    params,
  )

export const getTags = (name) =>
  call(`${BASE_URL}/tags/search`, { method: 'post' }, { type: { name } })

export const deleteTag = (tagSetId) =>
  call(`${BASE_URL}/tags`, { method: 'put' }, { tagSetId, onOff: false })

export const updateTag = (id, params) => call(`${BASE_URL}/tags/${id}`, { method: 'post' }, params)

export const createUserInbox = (params) =>
  call(`${BASE_URL}/users/inbox`, { method: 'post' }, params)

export const createLedgerRecord = (params) =>
  new Promise((resolve) => setTimeout(() => resolve(params), 1500))

export const deleteLedgerRecord = (guid) =>
  call(`${BASE_URL}/documents/${guid}`, { method: 'delete' })

export const getVendors = ({ ...params }) => {
  const { offset = 0, limit = DEFAULT_LIMIT, ...rest } = params
  return call(
    `${BASE_URL}/documents/merchants/search?offset=${offset || 0}&limit=${limit || DEFAULT_LIMIT}`,
    { method: 'post' },
    { filters: rest },
  )
}

export const getPurchaseOrder = (purchaseOrderId) =>
  call(`${BASE_URL}/exports/purchaseorders/${purchaseOrderId}`, { method: 'get' })

//https://pb5kywqf1k.execute-api.us-east-1.amazonaws.com/v1/emails/search?limit=20&offset=0
export const getInboxMails = ({ ...params }) => {
  const { offset, limit, ...rest } = params
  return call(`${BASE_URL}/emails/search?offset=${offset || 0}&limit=${limit || DEFAULT_LIMIT}`, {
    method: 'post',
    body: JSON.stringify(rest),
  })
}

//https://glh2itec4m.execute-api.us-east-1.amazonaws.com/v1/transactions/search?limit=20&offset=0
export const getTransactionData = ({ ...params }) => {
  const { offset, limit, ...rest } = params
  return call(
    `${BASE_URL}/transactions/search?offset=${offset || 0}&limit=${limit || DEFAULT_LIMIT}`,
    { method: 'post', body: JSON.stringify(rest) },
  )
}

export const updateArchiveTransactionData = ({ ...params }) => {
  return call(`${BASE_URL}/transactions/status`, { method: 'put', body: JSON.stringify(params) })
}

export const getArchiveTransactionData = ({ ...params }) => {
  const { offset, limit, ...rest } = params
  return call(
    `${BASE_URL}/transactions/search?offset=${offset || 0}&limit=${limit || DEFAULT_LIMIT}`,
    { method: 'post', body: JSON.stringify(rest) },
  )
}

export const getVendorsAlias = (type, ids) =>
  call(`${BASE_URL}/myvendors/search`, { method: 'post' }, { merchant: { ids, type } })

export const addVendorAlias = ({ ids, alias, type }) =>
  call(`${BASE_URL}/myvendors`, { method: 'post' }, { merchant: { ids, alias, type } })

export const changeVendorAlias = ({ ids, alias, type }) =>
  call(`${BASE_URL}/myvendors`, { method: 'put' }, { merchant: { ids, alias, type } })

export const deleteVendorAlias = ({ ids, alias, type }) =>
  call(`${BASE_URL}/myvendors`, { method: 'delete' }, { merchant: { ids, alias, type } })

export const getGLCodeMappingsForMyVendor = () =>
  call(
    `${BASE_URL}/tags/mappings/search`,
    { method: 'post' },
    {
      isMapped: 1,
      toTag: { type: { name: GL_CODE_TAG } },
      fromTag: { type: { name: MY_VENDOR_TAG } },
    },
  )

export const getExpenseCategoryMappingsForMyVendor = () =>
  call(
    `${BASE_URL}/tags/mappings/search`,
    { method: 'post' },
    {
      isMapped: 1,
      fromTag: { type: { name: MY_VENDOR_TAG } },
      toTag: { type: { name: EXPENSE_CATEGORY_TAG } },
    },
  )

export const mapTwoTags = (params) => call(`${BASE_URL}/tags/mappings`, { method: 'post' }, params)

export const changeTagMappings = (params) =>
  call(`${BASE_URL}/tags/mappings`, { method: 'put' }, params)

export const deleteTagMappings = (params) =>
  call(`${BASE_URL}/tags/mappings`, { method: 'delete' }, params)

export const getReportTemplates = ({ limit, offset, filters }) =>
  call(
    `${BASE_URL}/reports/templates/search?limit=${limit}&offset=${offset || 0}`,
    { method: 'post' },
    filters,
  )

export const getReports = ({ limit, offset, filters }) =>
  call(
    `${BASE_URL}/reports/search?limit=${limit}&offset=${offset || 0}`,
    { method: 'post' },
    filters,
  )

export const globalSearch = ({ params, filters }) => {
  return call(`${BASE_URL}/documents/search?${params}`, { method: 'post' }, { filters })
}

export const generateReport = (params) => call(`${BASE_URL}/reports`, { method: 'post' }, params)

export const deleteReport = (id) => call(`${BASE_URL}/reports/${id}`, { method: 'delete' })

export const updateReport = (id, params) =>
  call(`${BASE_URL}/reports/${id}`, { method: 'put' }, params)

export const sendPaswordResetLink = (params) =>
  call(
    `${BASE_URL}/recoverpassword`,
    {
      method: 'post',
      body: JSON.stringify(params),
    },
    '',
    false,
  )

export const changePassword = (params) =>
  call(
    `${BASE_URL}/resetpassword`,
    {
      method: 'post',
      body: JSON.stringify(params),
    },
    '',
    false,
  )

export const updatePassword = (params) =>
  call(
    `${BASE_URL}/users`,
    {
      method: 'put',
      body: JSON.stringify(params),
    },
    '',
  )

export const getCountries = () => call(`${BASE_URL}/countries`, { method: 'get' }, '', false)

export const getIndustries = () => call(`${BASE_URL}/tags/industries`, { method: 'get' }, '', false)

export const requestAdditionalEmailVerification = (data) =>
  call(
    `${BASE_URL}/recoverpassword`,
    {
      method: 'post',
      body: JSON.stringify({ username: data.email }),
    },
    '',
    false,
  )

// Data source API calls

export const getEmailProviders = () => call(`${BASE_URL}/emailproviders/search`, { method: 'post' })

export const getDataSources = () =>
  call(`${BASE_URL}/emailaccounts/search`, { method: 'post' }, { platform: 'web' })

// export const getDataSources = () => new Promise(resolve => setTimeout(() => resolve({ data: emailAccounts }), 1500))

export const syncDataSource = () => new Promise((resolve) => setTimeout(() => resolve(), 1500))

export const disconnectDataSource = (item) =>
  call(`${BASE_URL}/emailaccounts/${item.connectedEmail.id}`, { method: 'delete' })

export const connectDataSource = (params) =>
  call(
    `${BASE_URL}/emailaccounts`,
    { method: 'post' },
    {
      ...params,
      env: process.env.REACT_APP_ENVIRONMENT,
    },
  )

// Data integration API calls

export const getDataIntegrations = () =>
  new Promise((resolve) => setTimeout(() => resolve({ data: dataIntegrations }), 1500))
export const syncDataIntegration = () => new Promise((resolve) => setTimeout(() => resolve(), 1500))

// Search vendors api call
export const searchVendors = (name, type) => {
  return call(`${BASE_URL}/merchants/search`, { method: 'post' }, { type, name })
}

export const getSuppliers = (companyId) => {
  return call(`${BASE_URL}/merchants/search`, { method: 'post' }, { companyId, type: 'S' })
}

export const attachWorkflowToDocument = (workflowId, guids) => {
  return call(`${BASE_URL}/workflow/${workflowId}/attach`, { method: 'POST' }, { guids })
}

export const getAllWorkflows = (isActive) => {
  const qs = new URLSearchParams({ is_active: true })
  return call(`${BASE_URL}/workflow?${isActive ? qs.toString() : ''}`, { method: 'get' })
}

export const getWorkflowById = (workflowId) => {
  return call(`${BASE_URL}/workflow/${workflowId}`, { method: 'get' })
}

export const editWorkflow = (workflowId, workflow) => {
  return call(`${BASE_URL}/workflow/${workflowId}`, { method: 'PATCH' }, workflow)
}

export const deleteWorkflowStep = (workflowId, stepId) => {
  return call(`${BASE_URL}/workflow/${workflowId}/step/${stepId}`, { method: 'DELETE' })
}

export const createWorkflow = (workflow) => {
  return call(`${BASE_URL}/workflow`, { method: 'post' }, workflow)
}

export const bulkDeleteDocuments = (guids) => {
  return call(`${BASE_URL}/documents`, { method: 'DELETE' }, { guids })
}

export const transitionDocumentWorkflow = (guids, action) => {
  return call(`${BASE_URL}/workflow/transition`, { method: 'post' }, { guids, action })
}

export const getAssignedDocumentWorkflows = (filters) => {
  const qs = new URLSearchParams(filters)
  return call(`${BASE_URL}/workflow/assigned?${qs.toString()}`, { method: 'GET' })
}

// Create vendor api call
export const createVendor = (name, type) =>
  call(`${BASE_URL}/${type === 'O' ? 'ocrmerchants' : 'emerchants'}`, { method: 'post' }, { name })

export const searchVendorsMockApi = () =>
  new Promise((resolve) =>
    setTimeout(
      () =>
        resolve([
          { value: 'vendor1', label: 'Vendor - 1' },
          { value: 'vendor2', label: 'Vendor - 2' },
          { value: 'vendor3', label: 'Vendor - 3' },
          { value: 'vendor4', label: 'Vendor - 4' },
          { value: 'vendor5', label: 'Vendor - 5' },
          { value: 'vendor6', label: 'Vendor - 6' },
          { value: 'vendor7', label: 'Vendor - 7' },
          { value: 'vendor8', label: 'Vendor - 8' },
          { value: 'vendor8', label: 'Vendor - 9' },
          { value: 'vendor10', label: 'Vendor - 10' },
        ]),
      1500,
    ),
  )

export const updateExpense = (guid, data) => {
  return call(`${BASE_URL}/documents/${guid}`, { method: 'put' }, data)
}

export const searchOCRMerchants = (name, type) =>
  call(`${BASE_URL}/ocrmerchants/search`, { method: 'post' }, { name })

export const searchEMerchants = (name, type) =>
  call(`${BASE_URL}/emerchants/search`, { method: 'post' }, { name })

//********************************************************************************************** */
export const fetchUserRoles = () => {
  return call(`${BASE_URL}/roles`, { method: 'get' })
}

export const fetchManagedUsers = ({ companyID, limit, offset, ...request }) => {
  return call(`${BASE_URL}/companies/${companyID}/personas?limit=${limit}&offset=${offset}`, {
    method: 'get',
  })
}

export const updateManagedUser = ({ companyID, personaID, ...request }) => {
  return call(
    `${BASE_URL}/companies/${companyID}/personas/${personaID}`,
    { method: 'put' },
    request,
  )
}

export const addManagedUser = (request) => {
  return call(`${BASE_URL}/users/invite`, { method: 'post' }, request)
}

export const getAuthToken = async (email, token) => {
  const response = await fetch(`${BASE_URL}/sessions`, {
    method: 'post',
    headers: { authorization: `Basic ${btoa(`${email}:${token}`)}` },
  })

  return response.json()
}

export const registerManagedUser = (companyID, userID, token, request) => {
  return fetch(`${BASE_URL}/companies/${companyID}/personas/${userID}`, {
    method: 'put',
    headers: { authorization: `Basic ${btoa(`:${token}`)}` },
    body: JSON.stringify(request),
  })
}

export const fetchVendors = ({ limit = DEFAULT_LIMIT, offset = 0, ...filters }) => {
  return call(
    `${BASE_URL}/documents/merchants/search?limit=${limit}&offset=${offset}`,
    { method: 'post' },
    filters,
  )
}

export const fetchComplianceStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/compliance`, { method: 'post' }, filters)
}

export const fetchDocumentTypeStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/doctype`, { method: 'post' }, filters)
}

export const fetchPaymentStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/payment`, { method: 'post' }, filters)
}

export const fetchTotalUploadsStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/total`, { method: 'post' }, filters)
}

export const fetchAuditLogs = (filters) => {
  return call(`${BASE_URL}/documents/audit/search`, { method: 'post' }, filters)
}

export const fetchNewInvoicesStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/new-invoices`, { method: 'get' })
}

export const fetchPendingInvoicesStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/pending-invoices`, { method: 'get' })
}

export const fetchDiscountInvoicesStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/discounts`, { method: 'get' })
}

export const fetchPastDueInvoicesStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/past-due`, { method: 'get' })
}

export const fetchExceptionInvoicesStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/high-risk`, { method: 'get' })
}

export const fetchPOInvoicesStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/po-numbers`, { method: 'get' })
}

//https://pb5kywqf1k.execute-api.us-east-1.amazonaws.com/v1/stats/documents/top-vendors

export const fetchVendorsInvoicesStats = (filters) => {
  return call(`${BASE_URL}/stats/documents/top-vendors`, { method: 'get' })
}

export const fetchPaymentInvoicesStats = ({ limit = DEFAULT_LIMIT, offset = 0, ...filters }) => {
  return call(
    `${BASE_URL}/documents/search?limit=${limit}&offset=${offset}`,
    { method: 'post' },
    filters,
  )
}

//https://glh2itec4m.execute-api.us-east-1.amazonaws.com/v1/plaid/connections
export const fetchPlaidConnections = () => {
  return call(`${BASE_URL}/plaid/connections`, { method: 'get' })
}

export const fetchWorkatoConnections = () => {
  return call(`${BASE_URL}/dataintegrations/workato/connection`, { method: 'get' })
}

export const getRequestPromoCode = (data) =>
  call(
    `${BASE_URL}/requestpromocode`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    null,
    false,
  )

export const getDocsWorkflowSearch = (data) =>
  call(
    `${BASE_URL}/documents/workflows/search`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    null,
  )

export const getDocsWorkflowStepReview = (data) =>
  call(
    `${BASE_URL}/documents/workflows`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    null,
  )

export const getQuickBookConnection = (data) =>
  call(
    `${BASE_URL}/dataintegrations/workato/connection`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    null,
  )

export const syncExportDataIntegration = (data) =>
  call(
    `${BASE_URL}/dataintegrations/workato/sync`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    null,
  )

export const getExpenseGLCodesTag = (data) =>
  call(
    `${BASE_URL}/tags/mappings/search`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    null,
  )

export const getTagMappingsSearch = (data) =>
  call(
    `${BASE_URL}/tags/search`,
    {
      method: 'post',
      body: JSON.stringify(data),
    },
    null,
  )

export const getEntityTags = () => {
  return call(`${BASE_URL}/entities`, { method: 'get' })
}
